import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";

import { useSettingsContext, useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import {
  activateEmailNotifications,
  changeActiveStatus,
  disableEmailNotifications,
  getAdminSettings,
  getEmailTemplates,
  sendTestEmail,
} from "sj-ui-toolkit/src/api";

import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";

import { SendTestEmailDialog } from "@/components/email/SendTestEmailDialog";
import type { EmailTemplateData } from "@/components/email/types";

export function CommsEmails() {
  useBreadcrumb({
    label: "Emails",
    route: AdminAppRoutes.routes.comms.routes.email.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();

  const { isFeatureEnabled } = useSettingsContext();
  const { isRestrictedBundle } = useUserContext();

  const [settingsLoading, setSettingsLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [templates, setTemplates] = useState<EmailTemplateData[]>([]);
  const [selected, setSelected] = useState<EmailTemplateData[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateData | null>(null);

  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadSettings = async () => {
      try {
        const { emailNotifications } = await getAdminSettings();
        setEnabled(emailNotifications);
      } catch (error) {
        console.error("Failed to load email settings:", error);
        showToast({ severity: "error", detail: "Failed to load email settings" });
      } finally {
        setSettingsLoading(false);
      }
    };

    const loadTemplates = async () => {
      try {
        const templateData = await getEmailTemplates();
        setTemplates(templateData);
      } catch (error) {
        console.error("Failed to load email templates:", error);
        showToast({ severity: "error", detail: "Failed to load email templates" });
      } finally {
        setTemplatesLoading(false);
      }
    };

    loadSettings();
    loadTemplates();
  }, []);

  const filteredTemplates = useMemo(() => {
    const enabledTemplates = enabled
      ? templates.filter(t => t.name !== "confirmation_instructions")
      : templates.filter(
          t => t.name === "invitation_instructions" || t.name === "reconfirmation_instructions"
        );

    return !isRestrictedBundle && isFeatureEnabled(AdminAppRoutes.routes.tournaments.navFeature)
      ? enabledTemplates
      : enabledTemplates.filter(
          t => t.name !== "finished_tournament" && t.name !== "started_tournament"
        );
}, [templates, enabled, isRestrictedBundle, isFeatureEnabled]);

  const updateEnabledSetting = async (value: boolean) => {
    const previousEnabled = enabled;
    try {
      setEnabled(value);
      if (value) {
        setTemplatesLoading(true);
        await activateEmailNotifications();
        const templateData = await getEmailTemplates();
        setTemplates(templateData);
      } else {
        await disableEmailNotifications();
      }
      showToast({
        severity: "success",
        detail: `Email notifications ${value ? "enabled" : "disabled"}`,
      });
    } catch (error) {
      setEnabled(previousEnabled);
      console.error("Failed to update email notifications:", error);
      showToast({ severity: "error", detail: "Failed to update email notifications setting" });
    } finally {
      setTemplatesLoading(false);
    }
  };

  const onStatusChange = async (activated: boolean) => {
    const previousTemplates = templates;
    try {
      setTemplates(prev =>
        prev.map(t => (selected.find(sel => sel.id === t.id) ? { ...t, activated } : t))
      );
      await changeActiveStatus({
        ids: selected.map(template => template.id),
        activated,
      });
      setSelected([]);
      showToast({
        severity: "success",
        detail: `Template(s) ${activated ? "activated" : "deactivated"}`,
      });
    } catch (error) {
      setTemplates(previousTemplates);
      console.error("Failed to change template status:", error);
      showToast({ severity: "error", detail: "Failed to change template status" });
    }
  };

  const onSendTestEmail = async (email: string, templateName: string) => {
    try {
      await sendTestEmail({ email, templateName });
      showToast({ severity: "success", detail: `A test email has been sent to ${email}` });
    } catch (error) {
      console.error("Error sending test email:", error);
      showToast({ severity: "error", detail: "Failed to send test email" });
      throw error;
    }
  };

  const handleOpenDialog = (template: EmailTemplateData) => {
    setSelectedTemplate(template);
    setDialogVisible(true);
  };

  const selectable = useCallback(
    (e: { data: Record<string, unknown> }) =>
      e.data.name !== "invitation_instructions" && e.data.name !== "reconfirmation_instructions",
    []
  );

  const statusBody = useCallback(
    (rowData: EmailTemplateData) =>
      rowData.activated ? (
        <Tag severity="success" value="Active" />
      ) : (
        <Tag severity="danger" value="Inactive" />
      ),
    []
  );

  const actionsBody = useCallback(
    (rowData: EmailTemplateData) => (
      <div className="flex gap-4">
        <Link to={`${rowData.name}`}>
          <Button label="Edit" icon="pi pi-pencil" />
        </Link>
        <Button
          severity="secondary"
          label="Test"
          icon="pi pi-send"
          onClick={() => handleOpenDialog(rowData)}
        />
      </div>
    ),
    []
  );

  const SettingsSkeleton = () => (
    <Card title={<Skeleton width="30%" height="3rem" />}>
      <div className="flex flex-col gap-4">
        <Skeleton width="80%" height="2rem" />
        <Skeleton width="100%" height="4rem" />
      </div>
    </Card>
  );

  return (
    <>
      <div className="mb-4">
        <h1 className="sr-only">Emails</h1>
      </div>
      <div className="flex flex-col gap-8">
        {settingsLoading ? (
          <SettingsSkeleton />
        ) : (
          <Card
            title={
              <div className="flex justify-between items-center">
                <strong id="email-notifications">Email Notifications</strong>
                <InputSwitch
                  checked={enabled}
                  onChange={e => void updateEnabledSetting(e.value)}
                  aria-labelledby="email-notifications"
                />
              </div>
            }
          >
            <div className="flex flex-col gap-4">
              <p>
                Control whether email notifications are sent to users. Please note that regardless
                of this setting, the following emails will always be sent:
              </p>
              <ul className="list-disc list-inside ml-4">
                <li>Email Change Confirmation</li>
                <li>Invitation Instructions</li>
              </ul>
            </div>
          </Card>
        )}
        {templatesLoading ? (
          <SettingsSkeleton />
        ) : (
          <div className="flex flex-col w-full">
            <Card>
              <DataTable
                value={filteredTemplates}
                dataKey="id"
                aria-label="Email templates"
                selection={selected}
                selectionMode="multiple"
                sortField="activated"
                sortOrder={-1}
                isDataSelectable={selectable}
                onSelectionChange={e => setSelected(e.value as EmailTemplateData[])}
                emptyMessage="No email templates found."
                tableClassName="dt-responsive-text"
              >
                <Column selectionMode="multiple"></Column>
                <Column field="title" header="Title" sortable />
                <Column field="activated" header="Status" sortable body={statusBody} />
                <Column header="Actions" body={actionsBody} />
              </DataTable>

              <SendTestEmailDialog
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                onSend={onSendTestEmail}
                templateName={selectedTemplate?.name ?? ""}
                templateTitle={selectedTemplate?.title ?? ""}
              />
            </Card>
            {filteredTemplates.length > 0 && (
              <div className="flex gap-4 justify-end p-4 bg-elevation-2">
                <Button
                  label="Activate"
                  icon="pi pi-check"
                  severity="success"
                  disabled={selected.length === 0}
                  onClick={() => void onStatusChange(true)}
                />
                <Button
                  label="Deactivate"
                  icon="pi pi-times"
                  severity="danger"
                  disabled={selected.length === 0}
                  onClick={() => void onStatusChange(false)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
